import {
	TextStyle,
	IndicatorFormat,
	DashboardConfig,
	LongevityInput,
	PerformanceMetricsInput,
	DeductionItem,
	ReviewsInput,
	SalesInfoInput,
	SalesPerformanceInput,
	CardData,
	CustomerSatisfactionIndexInput,
} from "../../types/CardsGrid";
import { isEmpty } from "lodash";
import { getFormattedDate } from "@dealerwave/dw-layer-date-converter";

function smartTwoLine(text: string) {
	const t = (text ?? "").trim();
	if (!t) return t;

	// If it’s already short, don’t break.
	if (t.length <= 12) return t;

	const spaces: number[] = [];
	for (let i = 0; i < t.length; i++) if (t[i] === " ") spaces.push(i);

	// No spaces => can’t break intelligently
	if (spaces.length === 0) return t;

	const mid = t.length / 2;

	// Choose split point that minimizes the max(line lengths) and keeps them balanced
	let bestIdx = spaces[0];
	let bestScore = Number.POSITIVE_INFINITY;

	for (const idx of spaces) {
		const left = t.slice(0, idx).trim();
		const right = t.slice(idx + 1).trim();
		if (!left || !right) continue;

		const lenL = left.length;
		const lenR = right.length;

		// score: prioritize balance, then closeness to middle
		const balance = Math.abs(lenL - lenR);
		const maxLen = Math.max(lenL, lenR);
		const middleDist = Math.abs(idx - mid);

		const score = balance * 10 + maxLen * 2 + middleDist; // weights tuned for readability
		if (score < bestScore) {
			bestScore = score;
			bestIdx = idx;
		}
	}

	const left = t.slice(0, bestIdx).trim();
	const right = t.slice(bestIdx + 1).trim();

	// If the “best” split is still ugly (one side tiny), don’t split
	if (left.length < 2 || right.length < 2) return t;

	return `${left}\n${right}`;
}

const cx = (...parts: Array<string | false | undefined | null>) => parts.filter(Boolean).join(" ");

function applyTextStyle(style?: TextStyle) {
	if (!style) return "";
	const wantsTwoLine = Boolean(style.breakLine);
	return cx(
		style.bold ? "font-semibold" : "font-[500]",
		style.underline && "underline",
		style.italic && "italic",
		style.transform === "uppercase" && "uppercase",
		style.transform === "lowercase" && "lowercase",
		style.transform === "capitalize" && "capitalize",

		// IMPORTANT:
		// - if breakLine => allow newline rendering
		// - else => single line
		wantsTwoLine ? "whitespace-pre-line break-words" : "whitespace-nowrap",

		style.ellipsis && "truncate",
		style.align === "right" && "text-right",
		style.align === "center" && "text-center",
		(!style.align || style.align === "left") && "text-left"
	);
}

function formatIndicatorValue(raw: any, fmt: IndicatorFormat | undefined, cfg: DashboardConfig, timezone?: string) {
	if (raw === null || raw === undefined) return { text: "", isNegative: false };
	if (!fmt) return { text: String(raw), isNegative: false };

	const prefix = (fmt as any).prefix ?? "";
	const suffix = (fmt as any).suffix ?? "";

	const negativeStyle = cfg.numbers?.negativeStyle ?? "minus";

	const wrapNegative = (s: string, isNeg: boolean) => {
		if (!isNeg) return `${prefix}${s}${suffix}`;
		return negativeStyle === "parentheses" ? `${prefix}(${s})${suffix}` : `${prefix}-${s}${suffix}`;
	};

	switch (fmt.format) {
		case "currency": {
			const n = typeof raw === "number" ? raw : Number(raw);
			if (Number.isNaN(n)) return { text: `${prefix}${String(raw)}${suffix}`, isNegative: false };

			const isNeg = n < 0;
			const abs = Math.abs(n);

			const decimals = fmt.decimals ?? 2;
			const nf = new Intl.NumberFormat(undefined, {
				style: "currency",
				currency: fmt.currency ?? "USD",
				minimumFractionDigits: decimals,
				maximumFractionDigits: decimals,
			});

			// nf.format(abs) already includes the currency symbol
			return { text: wrapNegative(nf.format(abs), isNeg), isNegative: isNeg };
		}

		case "number": {
			const n = typeof raw === "number" ? raw : Number(raw);
			if (Number.isNaN(n)) return { text: `${prefix}${String(raw)}${suffix}`, isNegative: false };

			const isNeg = n < 0;
			const abs = Math.abs(n);

			const decimals = fmt.decimals ?? 0;
			const nf = new Intl.NumberFormat(undefined, {
				minimumFractionDigits: decimals,
				maximumFractionDigits: decimals,
			});

			return { text: wrapNegative(nf.format(abs), isNeg), isNegative: isNeg };
		}

		case "date": {
			const formattedDate = getFormattedDate(raw, timezone ?? "", fmt.pattern) || "-";
			return { text: `${prefix}${formattedDate}${suffix}`, isNegative: false };
		}

		case "datetime": {
			const formattedDate = getFormattedDate(raw, timezone ?? "", fmt.pattern) || "-";
			return { text: `${prefix}${formattedDate}${suffix}`, isNegative: false };
		}

		case "string":
		default:
			return { text: `${prefix}${String(raw)}${suffix}`, isNegative: false };
	}
}

function mapLongevityToCard(longevity: LongevityInput) {
	const sections = [
		{
			id: "longevity-a",
			title: "Hired Date",
			indicators: [
				{
					key: "hiredDate",
					title: "",
					value: longevity.hiredDate || "-",
				},
			],
		},
		{
			id: "longevity-c",
			title: "Longevity (Years)",
			indicators: [
				{
					key: "longevityYears",
					title: "",
					value: longevity.years,
				},
			],
		},
		{
			id: "longevity-b",
			title: "Termination Date",
			indicators: [
				{
					key: "terminationDate",
					title: "",
					value: longevity.terminationDate || "-",
				},
			],
		},
	];
	if (longevity.compensation) {
		sections.splice(2, 0, {
			id: "longevity-d",
			title: "Compensation",
			indicators: [
				{
					key: "compensation",
					title: "",
					value: longevity.compensation,
				},
			],
		});
	}
	return {
		id: "longevity",
		title: "Longevity",
		sections,
	};
}

function mapPerformanceMetricsToCard(perf: PerformanceMetricsInput) {
	return {
		id: "perf-metrics",
		title: "Performance Metrics",
		sections: [
			{
				id: "perf-a",
				title: "Back End PVR",
				indicators: [
					{
						key: "backEndPvr",
						title: "",
						value: perf.backendPvr,
					},
				],
			},
			{
				id: "perf-b",
				title: "Unit Adjustments",
				indicators: [
					{
						key: "unitAdj",
						title: "",
						value: `(Cash: +${perf.unitsAdjustments?.cash ?? 0}, Lease: +${perf.unitsAdjustments?.lease ?? 0})`,
					},
				],
			},
			{
				id: "perf-c",
				title: "Unfunded Deals (MTD)",
				indicators: [
					{
						key: "unfundedDeals",
						title: "",
						value: perf.unfundedDeals,
					},
				],
			},
		],
	};
}

const toCamelCase = (str: string) => str.toLowerCase().replace(/[^a-z0-9]+(.)/g, (_, chr) => chr.toUpperCase());

function mapDeductionsToCard(items: DeductionItem[]) {
	return {
		id: "deductions",
		title: "Deductions",
		sections: (items ?? []).map((item) => ({
			id: item.title,
			title: item.title,
			indicators: [
				{
					key: toCamelCase(item.title),
					title: "",
					value: item.value,
				},
			],
		})),
	};
}

function mapReviewsToCard(data: ReviewsInput[], reviewsCount: number) {
	return {
		id: "reviews",
		title: `Reviews: ${reviewsCount}`,
		sections: [
			{
				id: "reviews-a",
				title: "Franchise",
				indicators: data.map((item) => {
					return {
						key: "franchise",
						title: "",
						value: item.make,
					};
				}),
			},
			{
				id: "reviews-b",
				title: "Required Reviews",
				indicators: data.map((item) => {
					return {
						key: "requiredReviews",
						title: "",
						value: item.requiredReviews,
					};
				}),
			},
			{
				id: "reviews-c",
				title: "Actual Penetration",
				indicators: data.map((item) => {
					return {
						key: "actualPenetration",
						title: "",
						value: item.penetration,
					};
				}),
			},
		],
	};
}

const getProduct = (products: { product: string; total: number | string }[] = [], product = "Front Gross Profit") =>
	products.find((item) => item.product === product);

const getFrontEndPVR = ({
	salesTotals,
	units = 0,
}: {
	salesTotals: { frontGrossProfit: number | string; frontProducts?: { product: string; total: number | string }[] };
	units: number;
}) => {
	if (!salesTotals || !units) {
		return 0;
	}

	const { frontGrossProfit = 0, frontProducts = [] } = salesTotals || {};
	const frontGrossProduct = getProduct(frontProducts);

	if (!frontGrossProduct) {
		return Number(frontGrossProfit) / Number(units);
	}

	const { total = 0 } = frontGrossProduct;
	return Number(total) / Number(units);
};

function mapSalesInfoToCard(salesPerformance: SalesPerformanceInput) {
	const frontEndPVR = getFrontEndPVR({ salesTotals: salesPerformance?.totals, units: salesPerformance?.totals?.units });
	let backOuts = 0;
	salesPerformance?.sales?.forEach((sale) => {
		if (sale.units < 0) {
			backOuts = Number(backOuts) + Number(sale.units);
		}
	});
	const data: SalesInfoInput = {
		frontEndPVR,
		backOuts: Math.abs(backOuts),
	};
	return {
		id: "sales-info",
		title: "Sales Information",
		sections: [
			{
				id: "sales-info-a",
				title: "Front End PVR",
				indicators: [
					{
						key: "frontEndPVR",
						title: "",
						value: data.frontEndPVR,
					},
				],
			},
			{
				id: "sales-info-b",
				title: "Recon / Backouts",
				indicators: [
					{
						key: "reconBackouts",
						title: "",
						value: data.backOuts,
					},
				],
			},
		],
	};
}

const resolveCardsData = ({
	longevity,
	performanceMetrics,
	deductions,
	customerSatisfactionIndex,
	salesPerformance,
}: {
	longevity?: LongevityInput;
	performanceMetrics?: PerformanceMetricsInput;
	deductions?: DeductionItem[];
	customerSatisfactionIndex?: CustomerSatisfactionIndexInput;
	salesPerformance?: SalesPerformanceInput;
}) => {
	const cardsData: CardData[] = [];
	if (salesPerformance && !isEmpty(salesPerformance)) {
		cardsData.push(mapSalesInfoToCard(salesPerformance));
	}
	if (performanceMetrics && !isEmpty(performanceMetrics)) {
		cardsData.push(mapPerformanceMetricsToCard(performanceMetrics));
	}
	if (longevity && !isEmpty(longevity)) {
		cardsData.push(mapLongevityToCard(longevity));
	}
	if (customerSatisfactionIndex && !isEmpty(customerSatisfactionIndex)) {
		cardsData.push(mapReviewsToCard(customerSatisfactionIndex?.makes || [], customerSatisfactionIndex?.totalReviews || 0));
	}
	if (deductions?.length) {
		cardsData.push(mapDeductionsToCard(deductions));
	}
	return cardsData;
};

function minSpanFromSections(sectionCount: number): number {
	const spanMap: Record<number, number> = {
		0: 2,
		1: 2,
		2: 3,
		3: 4,
		4: 5,
		5: 6,
		6: 7,
		7: 8,
		8: 9,
		9: 10,
		10: 11,
	};
	return spanMap[sectionCount] ?? 12;
}

function baseShouldScroll(sectionCount: number): boolean {
	return sectionCount > 10; // ✅ always true if >10
}

/**
 * packRows: greedy packing using MIN spans (preserves order).
 * NOTE: we return rows as arrays of indexes, so we can "pull" next cards later.
 */
function packRows(cards: CardData[], maxCols = 12): number[][] {
	const rows: number[][] = [];
	let row: number[] = [];
	let used = 0;

	for (let i = 0; i < cards.length; i++) {
		const minSpan = Math.min(minSpanFromSections(cards[i].sections.length), maxCols);

		if (row.length > 0 && used + minSpan > maxCols) {
			rows.push(row);
			row = [];
			used = 0;
		}

		row.push(i);
		used += minSpan;
	}

	if (row.length) rows.push(row);
	return rows;
}

function soloMaxSpan(sectionCount: number): number {
	const min = minSpanFromSections(sectionCount);

	// allow growth but not infinite
	return Math.min(12, min);
}

/**
 * expandRowSpans: given a finalized row (indexes), compute spans + per-card shouldScroll overrides.
 * Special rules:
 * - solo row:
 *   - if sections < 6 => span 6
 *   - else => span 12
 * - multi card row: expand evenly to fill 12
 */
function expandRowSpans(cards: CardData[], rowIdx: number[], maxCols = 12) {
	const rowCards = rowIdx.map((i) => cards[i]);
	const mins = rowCards.map((c) => Math.min(minSpanFromSections(c.sections.length), maxCols));
	const forcedScroll = rowCards.map(() => false);

	const minSum = mins.reduce((a, b) => a + b, 0);
	if (minSum >= maxCols) return { spans: mins, forcedScroll };

	const leftover = maxCols - minSum;
	const n = rowCards.length;

	const addEach = Math.floor(leftover / n);
	let rem = leftover % n;

	let spans = mins.map((s) => {
		const add = addEach + (rem > 0 ? 1 : 0);
		if (rem > 0) rem--;
		return s + add;
	});

	if (n === 1) {
		const sc = rowCards[0].sections.length;
		const maxAllowed = soloMaxSpan(sc);
		spans[0] = Math.min(spans[0], maxAllowed);
	}

	return { spans, forcedScroll };
}

/**
 * computeSmartSpans:
 * 1) pack by mins
 * 2) apply your new edge case: solo-small row can "pull" next large card to fill 6+6
 * 3) expand spans
 * 4) compute shouldScroll with overrides:
 *    - if sectionCount > 10 => true
 *    - if pulled-large => true
 *    - else false
 */
function computeSmartSpans<T extends CardData>(cards: T[], maxCols = 12): Array<T & { span: number; shouldScroll: boolean }> {
	const initialRows = packRows(cards, maxCols);

	// We'll build final rows, possibly merging by "pulling" from the next row.
	const finalRows: number[][] = [];

	for (let r = 0; r < initialRows.length; r++) {
		const row = initialRows[r];

		// If row is a solo card and it's "small" (<6 sections) => it would become span 6.
		// If there is a next row and its first card is "large" (>5 sections), pull it into this row.
		if (row.length === 1) {
			const idxA = row[0];
			const sectionsA = cards[idxA].sections.length;

			const nextRow = initialRows[r + 1];
			const hasNextFirst = nextRow && nextRow.length > 0;

			if (sectionsA < 6 && hasNextFirst) {
				const idxB = nextRow[0];
				const sectionsB = cards[idxB].sections.length;

				if (sectionsB > 5) {
					// ✅ make this row [A, B] and remove B from the next row
					finalRows.push([idxA, idxB]);

					// mutate next row to drop its first item
					nextRow.shift();

					// if next row becomes empty, skip it
					if (nextRow.length === 0) r += 1;

					continue;
				}
			}
		}

		// otherwise keep the row as-is
		finalRows.push(row);
	}

	// Now compute spans + shouldScroll
	const out: Array<T & { span: number; shouldScroll: boolean }> = [];

	for (const rowIdx of finalRows) {
		// special forced case: a 2-card row created by pulling from solo-small
		let pulledLargeIndexInRow: number | null = null;
		if (rowIdx.length === 2) {
			const a = cards[rowIdx[0]].sections.length;
			const b = cards[rowIdx[1]].sections.length;
			if (a < 6 && b > 5) pulledLargeIndexInRow = 1;
		}

		// If it's the pulled-pair case, force spans 6 + 6 and force scroll for the large card
		if (pulledLargeIndexInRow === 1) {
			for (let i = 0; i < 2; i++) {
				const c = cards[rowIdx[i]] as T;
				const sc = c.sections.length;

				out.push({
					...c,
					span: 6,
					shouldScroll: i === 1 ? true : baseShouldScroll(sc), // ✅ large forced true, small only >10
				});
			}
			continue;
		}

		// Normal expansion logic
		const { spans } = expandRowSpans(cards, rowIdx, maxCols);

		for (let i = 0; i < rowIdx.length; i++) {
			const c = cards[rowIdx[i]] as T;
			const sc = c.sections.length;

			out.push({
				...c,
				span: spans[i],
				shouldScroll: baseShouldScroll(sc),
			});
		}
	}

	return out;
}

export {
	cx,
	smartTwoLine,
	applyTextStyle,
	formatIndicatorValue,
	mapLongevityToCard,
	mapPerformanceMetricsToCard,
	mapDeductionsToCard,
	mapReviewsToCard,
	mapSalesInfoToCard,
	resolveCardsData,
	minSpanFromSections,
	packRows,
	soloMaxSpan,
	expandRowSpans,
	computeSmartSpans,
};
